import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useId, type RefObject } from 'react'
import type { RecordSessionInfo } from '@companion-app/shared/Model/ActionRecorderModel.js'
import type { DropdownChoice, DropdownChoiceId } from '@companion-app/shared/Model/Common.js'
import { Button, ButtonGroup } from '~/Components/Button'
import { Form, FormLabel } from '~/Components/Form.js'
import type { GenericConfirmModalRef } from '~/Components/GenericConfirmModal.js'
import { Grid } from '~/Components/Grid'
import { MultiDropdownInputField } from '~/Components/MultiDropdownInputField.js'
import { SwitchInputField } from '~/Components/SwitchInputField'
import { trpc, useMutationExt } from '~/Resources/TRPC.js'
import { PreventDefaultHandler, useComputed } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'

interface RecorderSessionHeadingProps {
	confirmRef: RefObject<GenericConfirmModalRef | null>
	sessionInfo: RecordSessionInfo
	doFinish: () => void
}

export const RecorderSessionHeading = observer(function RecorderSessionHeading({
	confirmRef,
	sessionInfo,
	doFinish,
}: RecorderSessionHeadingProps) {
	const { connections } = useContext(RootAppStoreContext)

	const discardActionsMutation = useMutationExt(trpc.actionRecorder.session.discardActions.mutationOptions())
	const abortSessionMutation = useMutationExt(trpc.actionRecorder.session.abort.mutationOptions())
	const setRecordingMutation = useMutationExt(trpc.actionRecorder.session.setRecording.mutationOptions())
	const setConnectionsMutation = useMutationExt(trpc.actionRecorder.session.setConnections.mutationOptions())

	const sessionId = sessionInfo.id
	const doClearActions = useCallback(() => {
		discardActionsMutation.mutateAsync({ sessionId }).catch((e) => {
			console.error(e)
		})
	}, [discardActionsMutation, sessionId])

	const doAbort = useCallback(() => {
		if (confirmRef.current) {
			confirmRef.current.show(
				'Discard session',
				'Are you sure you wish to discard the current session?',
				'Discard',
				() => {
					abortSessionMutation.mutateAsync({ sessionId }).catch((e) => {
						console.error(e)
					})
				}
			)
		}
	}, [abortSessionMutation, sessionId, confirmRef])

	const changeRecording = useCallback(
		(isRunning: boolean) => {
			setRecordingMutation.mutateAsync({ sessionId, isRunning }).catch((e) => {
				console.error(e)
			})
		},
		[setRecordingMutation, sessionId]
	)

	const doFinish2 = useCallback(() => {
		changeRecording(false)

		doFinish()
	}, [changeRecording, doFinish])

	const changeConnectionIds = useCallback(
		(ids: DropdownChoiceId[]) => {
			const connectionIds = ids.map((id) => String(id))
			setConnectionsMutation.mutateAsync({ sessionId, connectionIds }).catch((e) => {
				console.error(e)
			})
		},
		[setConnectionsMutation, sessionId]
	)

	const connectionsWhichCanRecord = useComputed(() => {
		const result: DropdownChoice[] = []

		for (const [id, info] of connections.connections.entries()) {
			if (info.hasRecordActionsHandler) {
				result.push({
					id,
					label: info.label,
				})
			}
		}

		return result
	}, [connections])

	const connectionsFieldId = useId()
	const recordingFieldId = useId()

	return (
		<>
			<Form onSubmit={PreventDefaultHandler}>
				<Grid.Row className="flex-form m-0 clear-both">
					<div className="flex w-full gap-6">
						<div className="w-full">
							<FormLabel htmlFor={connectionsFieldId}>Connections</FormLabel>
							<MultiDropdownInputField
								htmlName={connectionsFieldId}
								value={sessionInfo.connectionIds}
								setValue={changeConnectionIds}
								choices={connectionsWhichCanRecord}
							/>
						</div>

						<div>
							<FormLabel htmlFor={recordingFieldId}>Recording</FormLabel>
							<br />
							<SwitchInputField id={recordingFieldId} value={!!sessionInfo.isRunning} setValue={changeRecording} />
						</div>
					</div>
				</Grid.Row>

				<Grid.Row className="m-0 clear-both">
					<div>
						<ButtonGroup className="mb-4">
							<Button onClick={doClearActions} color="secondary" disabled={!sessionInfo.actions?.length}>
								Clear Actions
							</Button>
							<Button onClick={doAbort} color="danger">
								Discard
							</Button>
							<Button onClick={doFinish2} color="secondary" disabled={!sessionInfo.actions?.length}>
								Finish
							</Button>
						</ButtonGroup>
					</div>
				</Grid.Row>
			</Form>
		</>
	)
})
